import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronUp, Search, Loader2, TrendingUp,
  Trophy, AlertTriangle, ShoppingCart, Euro, Phone,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, ReferenceDot, BarChart, Bar,
} from 'recharts';
import {
  getCompareCyclePriceHistory,
  getCompareCycleDailyMetrics,
  getCompareCycleTransactionsCategorized,
  runCompareCycleSnapshotNow,
} from '@/services/api';

const GRADES = ['A', 'A-', 'B', 'C', 'C-', 'D', 'D-'];
const PRESET_PERIODS = [
  { label: '7j', days: 7 },
  { label: '15j', days: 15 },
  { label: '30j', days: 30 },
  { label: '2 mois', days: 60 },
  { label: '3 mois', days: 90 },
  { label: '6 mois', days: 180 },
  { label: '12 mois', days: 365 },
];

function StatusBadge({ status, rank }) {
  if (status === 'winning') {
    return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-0.5"><Trophy className="w-3 h-3" />#1</Badge>;
  }
  if (status === 'losing') {
    return <Badge variant="outline" className="border-amber-300 text-amber-700 gap-0.5"><AlertTriangle className="w-3 h-3" />#{rank}</Badge>;
  }
  return <Badge variant="outline" className="border-slate-300 text-slate-500">Hors top</Badge>;
}

function GradeChart({ grade, points, metricsByDate }) {
  if (!points || points.length === 0) return <div className="h-48 flex items-center justify-center text-sm text-slate-400">Aucune donnée historique pour le grade {grade}</div>;
  const series = points.map(p => ({
    date: p.date,
    dateShort: p.date.slice(5),
    my_price: p.my_price,
    best_price: p.best_price,
    my_rank: p.my_rank,
    status: p.status,
    phones_won: metricsByDate?.[p.date]?.phones_won || 0,
    revenue: metricsByDate?.[p.date]?.revenue || 0,
  }));
  const winningPoints = series.filter(p => p.status === 'winning');
  const lastPoint = series[series.length - 1];

  return (
    <div>
      <div className="flex items-baseline gap-3 mb-2 px-1">
        <span className="text-xs font-semibold text-slate-700">Grade {grade}</span>
        <StatusBadge status={lastPoint.status} rank={lastPoint.my_rank} />
        {lastPoint.my_price !== null && lastPoint.my_price !== undefined && (
          <span className="text-xs text-slate-500">Mon prix: <strong className="text-slate-700">{lastPoint.my_price}€</strong></span>
        )}
        {lastPoint.best_price && lastPoint.status !== 'winning' && (
          <span className="text-xs text-slate-500">Prix gagnant: <strong className="text-emerald-700">{lastPoint.best_price}€</strong></span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={series} margin={{ top: 10, right: 8, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="dateShort" tick={{ fontSize: 10, fill: '#64748b' }} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
          <Tooltip
            contentStyle={{ fontSize: 11, padding: '6px 8px' }}
            labelFormatter={(v, payload) => payload?.[0]?.payload?.date || v}
            formatter={(value, name) => {
              if (name === 'Mon prix') return [value !== null && value !== undefined ? `${value}€` : '—', name];
              if (name === 'Prix gagnant') return [value !== null && value !== undefined ? `${value}€` : '—', name];
              return [value, name];
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
          <Line type="monotone" dataKey="my_price" name="Mon prix" stroke="#0d9488" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
          <Line type="monotone" dataKey="best_price" name="Prix gagnant" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 4" dot={false} connectNulls={true} />
          {winningPoints.map((p, i) => (
            <ReferenceDot key={i} x={p.dateShort} y={p.my_price} r={4} fill="#10b981" stroke="#fff" strokeWidth={1.5} ifOverflow="extendDomain" />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ProductHistoryCard({ product, days, metricsByDate, isOpen, onToggle }) {
  const productId = String(product.idproduct);
  const [seriesData, setSeriesData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const data = await getCompareCyclePriceHistory(productId, days);
        if (!ignore) setSeriesData(data?.series || {});
      } catch {
        if (!ignore) toast.error(`Erreur historique pour ${product.product}`);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [productId, days, isOpen, product.product]);

  return (
    <Card className="border-slate-200">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors text-left"
        data-testid={`cc-hist-product-${productId}`}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-slate-800">{product.product || product.name}</span>
          <Badge variant="outline" className="text-[10px]">{product.type}</Badge>
          <span className="text-xs text-slate-400">{product.manufacturer}</span>
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {isOpen && (
        <CardContent className="border-t border-slate-100 pt-3 pb-4 space-y-4">
          {loading && <div className="h-48 flex items-center justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>}
          {!loading && seriesData && GRADES.map(g => (
            <GradeChart key={g} grade={g} points={seriesData[g] || []} metricsByDate={metricsByDate} />
          ))}
        </CardContent>
      )}
    </Card>
  );
}

function MetricsOverview({ metrics, days, onSnapshotNow, snapshotting }) {
  const totalPhones = metrics.reduce((s, m) => s + (m.phones_won || 0), 0);
  const totalRevenue = metrics.reduce((s, m) => s + (m.revenue || 0), 0);
  const avgPhonesDay = totalPhones / Math.max(1, metrics.length);
  // Build chart data: ensure we have every day
  const chartData = metrics.map(m => ({
    date: m.date,
    dateShort: (m.date || '').slice(5),
    phones: m.phones_won || 0,
    revenue: m.revenue || 0,
  }));

  return (
    <Card className="border-slate-200">
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-baseline gap-4">
            <h3 className="text-sm font-semibold text-slate-800">Téléphones remportés ({days}j)</h3>
            <div className="flex items-center gap-3 text-xs text-slate-600">
              <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-emerald-600" /><strong>{totalPhones}</strong> téléphones</span>
              <span className="flex items-center gap-1"><Euro className="w-3.5 h-3.5 text-emerald-600" /><strong>{totalRevenue.toFixed(2)}€</strong> CA</span>
              <span className="text-slate-400">≈ {avgPhonesDay.toFixed(1)}/j</span>
            </div>
          </div>
          <Button
            onClick={onSnapshotNow}
            disabled={snapshotting}
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1"
            data-testid="cc-hist-snapshot-now-btn"
          >
            {snapshotting ? <Loader2 className="w-3 h-3 animate-spin" /> : <TrendingUp className="w-3 h-3" />}
            Snapshot maintenant
          </Button>
        </div>
        {chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={chartData} margin={{ top: 5, right: 8, left: -15, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="dateShort" tick={{ fontSize: 10, fill: '#64748b' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
              <Tooltip
                contentStyle={{ fontSize: 11, padding: '6px 8px' }}
                formatter={(value, name) => {
                  if (name === 'CA') return [`${value.toFixed(2)}€`, name];
                  return [value, name];
                }}
              />
              <Bar dataKey="phones" name="Phones" fill="#0d9488" radius={[3, 3, 0, 0]} />
              <Bar dataKey="revenue" name="CA" fill="#f59e0b" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

function TransactionSection({ title, icon: Icon, transactions, color, emptyMsg, showGradeChange }) {
  if (transactions.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-3 px-4">
          <div className="flex items-center gap-2 mb-1">
            <Icon className={`w-4 h-4 ${color}`} />
            <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
            <Badge variant="outline" className="text-[10px]">0</Badge>
          </div>
          <p className="text-xs text-slate-400">{emptyMsg}</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-slate-200">
      <CardContent className="py-3 px-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon className={`w-4 h-4 ${color}`} />
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
          <Badge className="text-[10px]">{transactions.length}</Badge>
        </div>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {transactions.map(t => (
            <div key={t.transaction_number} className="flex items-center justify-between text-xs px-2 py-1.5 hover:bg-slate-50 rounded">
              <div className="flex items-center gap-2 min-w-0">
                <code className="text-[10px] text-slate-500 shrink-0">{t.transaction_number}</code>
                <span className="font-medium text-slate-700 truncate">{t.model}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {showGradeChange ? (
                  <span className="text-[11px]">
                    <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">{t.original_grade}</Badge>
                    <span className="mx-1 text-slate-400">→</span>
                    <Badge className="text-[10px] bg-orange-100 text-orange-800">{t.validated_grade}</Badge>
                  </span>
                ) : (
                  <Badge variant="outline" className="text-[10px]">{t.original_grade || '—'}</Badge>
                )}
                <span className="text-slate-600 font-mono">{t.original_amount || '—'}€</span>
                <span className="text-slate-400 text-[10px]">{t.create_date}</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function CompareCycleHistoriqueTab({ catalog }) {
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [modelFilter, setModelFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('Mobile');
  const [storageFilter, setStorageFilter] = useState('all');
  const [onlyWithPrice, setOnlyWithPrice] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [metricsByDate, setMetricsByDate] = useState({});
  const [transactionsCat, setTransactionsCat] = useState({ today_promises: [], pending_grade_change: [] });
  const [snapshotting, setSnapshotting] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 25;

  // Load metrics + categorized transactions on mount or when days changes
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const [m, txCat] = await Promise.all([
          getCompareCycleDailyMetrics(days),
          getCompareCycleTransactionsCategorized(),
        ]);
        if (ignore) return;
        const list = m.metrics || [];
        setMetrics(list);
        const byDate = {};
        for (const x of list) byDate[x.date] = x;
        setMetricsByDate(byDate);
        setTransactionsCat({
          today_promises: txCat.today_promises || [],
          pending_grade_change: txCat.pending_grade_change || [],
        });
      } catch (e) {
        if (!ignore) toast.error('Erreur chargement metrics');
      }
    })();
    return () => { ignore = true; };
  }, [days]);

  // Extract storage capacity from product name (e.g. "iPhone 14 Pro 128Go" -> "128Go")
  const extractStorage = (name) => {
    if (!name) return null;
    const m = name.match(/(\d+)\s*(Go|To|GB|TB)\b/i);
    if (!m) return null;
    let val = parseInt(m[1]);
    const unit = m[2].toLowerCase();
    if (unit === 'to' || unit === 'tb') val *= 1024;
    return `${val}Go`;
  };

  // Available filter values from catalog
  const { brands, types, models, storages } = useMemo(() => {
    const b = {}, t = {}, m = {}, s = {};
    for (const p of catalog) {
      b[p.manufacturer] = (b[p.manufacturer] || 0) + 1;
      t[p.type] = (t[p.type] || 0) + 1;
    }
    for (const p of catalog) {
      if (brandFilter !== 'all' && p.manufacturer !== brandFilter) continue;
      if (typeFilter !== 'all' && p.type !== typeFilter) continue;
      const mod = p.product_model || p.product || '';
      if (mod) m[mod] = (m[mod] || 0) + 1;
    }
    for (const p of catalog) {
      if (brandFilter !== 'all' && p.manufacturer !== brandFilter) continue;
      if (typeFilter !== 'all' && p.type !== typeFilter) continue;
      if (modelFilter !== 'all' && (p.product_model || '') !== modelFilter) continue;
      const stor = extractStorage(p.product);
      if (stor) s[stor] = (s[stor] || 0) + 1;
    }
    return { brands: b, types: t, models: m, storages: s };
  }, [catalog, brandFilter, typeFilter, modelFilter]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return catalog.filter(p => {
      if (typeFilter !== 'all' && p.type !== typeFilter) return false;
      if (brandFilter !== 'all' && p.manufacturer !== brandFilter) return false;
      if (modelFilter !== 'all' && (p.product_model || '') !== modelFilter) return false;
      if (storageFilter !== 'all' && extractStorage(p.product) !== storageFilter) return false;
      if (onlyWithPrice && parseFloat(p['price grade A'] || 0) === 0) return false;
      if (!q) return true;
      return (p.product || '').toLowerCase().includes(q) ||
        (p.manufacturer || '').toLowerCase().includes(q) ||
        (p.product_model || '').toLowerCase().includes(q);
    });
  }, [catalog, search, brandFilter, modelFilter, typeFilter, storageFilter, onlyWithPrice]);

  const paginated = useMemo(() => filtered.slice((page - 1) * perPage, page * perPage), [filtered, page]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));

  const handleSnapshotNow = async () => {
    setSnapshotting(true);
    try {
      const r = await runCompareCycleSnapshotNow();
      toast.success(`Snapshot enregistré: ${r.products_snapshot} produits, ${r.metrics?.phones_won || 0} phones du jour`);
      // Reload metrics
      const m = await getCompareCycleDailyMetrics(days);
      const list = m.metrics || [];
      setMetrics(list);
      const byDate = {};
      for (const x of list) byDate[x.date] = x;
      setMetricsByDate(byDate);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur snapshot'); }
    finally { setSnapshotting(false); }
  };

  return (
    <div className="space-y-3 mt-3">
      {/* Period selector + metrics overview */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-500 font-medium">Période :</span>
        {PRESET_PERIODS.map(p => (
          <Button
            key={p.days}
            variant={days === p.days ? 'default' : 'outline'}
            size="sm"
            className="h-7 text-xs px-3"
            onClick={() => setDays(p.days)}
            data-testid={`cc-hist-period-${p.days}`}
          >{p.label}</Button>
        ))}
        <span className="text-xs text-slate-400">ou personnalisé:</span>
        <Input
          type="number"
          min="1"
          max="730"
          value={days}
          onChange={e => setDays(Math.max(1, Math.min(730, parseInt(e.target.value) || 30)))}
          className="h-7 w-20 text-xs"
          data-testid="cc-hist-period-custom"
        />
        <span className="text-xs text-slate-400">jours</span>
      </div>

      <MetricsOverview metrics={metrics} days={days} onSnapshotNow={handleSnapshotNow} snapshotting={snapshotting} />

      {/* Transaction sections (today's promises + pending grade-change) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <TransactionSection
          title="Promesses du jour"
          icon={ShoppingCart}
          transactions={transactionsCat.today_promises}
          color="text-blue-600"
          emptyMsg="Aucune promesse saisie aujourd'hui."
          showGradeChange={false}
        />
        <TransactionSection
          title="En attente de mise à dispo (grade révisé)"
          icon={AlertTriangle}
          transactions={transactionsCat.pending_grade_change}
          color="text-orange-600"
          emptyMsg="Aucune transaction en attente avec grade révisé."
          showGradeChange={true}
        />
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="py-2 px-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input placeholder="Rechercher produit..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="pl-8 h-8 text-sm" data-testid="cc-hist-search" />
          </div>
          <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
            className="h-8 px-2 text-xs border border-slate-200 rounded bg-white" data-testid="cc-hist-type">
            <option value="all">Tous types</option>
            {Object.entries(types).map(([t, n]) => <option key={t} value={t}>{t} ({n})</option>)}
          </select>
          <select value={brandFilter} onChange={e => { setBrandFilter(e.target.value); setModelFilter('all'); setStorageFilter('all'); setPage(1); }}
            className="h-8 px-2 text-xs border border-slate-200 rounded bg-white" data-testid="cc-hist-brand">
            <option value="all">Toutes marques</option>
            {Object.entries(brands).sort((a,b) => b[1] - a[1]).map(([b, n]) => <option key={b} value={b}>{b} ({n})</option>)}
          </select>
          <select value={modelFilter} onChange={e => { setModelFilter(e.target.value); setStorageFilter('all'); setPage(1); }}
            className="h-8 px-2 text-xs border border-slate-200 rounded bg-white max-w-[180px]" data-testid="cc-hist-model">
            <option value="all">Tous modèles ({Object.keys(models).length})</option>
            {Object.entries(models).sort().map(([m, n]) => <option key={m} value={m}>{m} ({n})</option>)}
          </select>
          <select value={storageFilter} onChange={e => { setStorageFilter(e.target.value); setPage(1); }}
            className="h-8 px-2 text-xs border border-slate-200 rounded bg-white" data-testid="cc-hist-storage">
            <option value="all">Toutes capacités ({Object.keys(storages).length})</option>
            {Object.entries(storages).sort((a,b) => parseInt(a[0]) - parseInt(b[0])).map(([s, n]) => <option key={s} value={s}>{s} ({n})</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={onlyWithPrice} onChange={e => setOnlyWithPrice(e.target.checked)} className="w-3.5 h-3.5" data-testid="cc-hist-mes-prix" />
            Mes prix uniquement
          </label>
          <span className="text-xs text-slate-500">{filtered.length} produits</span>
        </CardContent>
      </Card>

      {/* Product list */}
      <div className="space-y-2">
        {paginated.map(p => (
          <ProductHistoryCard
            key={p.idproduct}
            product={p}
            days={days}
            metricsByDate={metricsByDate}
            isOpen={openId === p.idproduct}
            onToggle={() => setOpenId(prev => prev === p.idproduct ? null : p.idproduct)}
          />
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-sm text-slate-400">Aucun produit trouvé. Ajustez vos filtres.</div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1.5 py-3">
          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage(1)}>1</Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Préc</Button>
          <span className="text-xs text-slate-500 px-2">Page {page}/{totalPages}</span>
          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Suiv</Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>{totalPages}</Button>
        </div>
      )}
    </div>
  );
}
